import { useState, useMemo, useEffect, useRef } from 'react'
import { Drawer, IconButton, Typography, Box, Stack, Button, Collapse, Divider } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import { useForm } from 'react-hook-form'
import { TextField } from '@common/components/Form/TextField'
import { Dropdown, Option } from '@common/components/Form/Dropdown/Dropdown'
import RadioGroup from '@common/components/Form/RadioGroup'
import ConfirmationDialog from '@common/components/ConfirmationDialog/ConfirmationDialog'
import { SPACING } from '@common/theme/spacing'
import { SIZING } from '@common/theme/sizing'
import { useStudyQueryMetadata } from '../hooks/useStudyQueriesMetadata'
import { CreateQueryFormData, CreateQueryValidationSchema } from '../schemas/queryValidationSchema'
import { zodResolver } from '@hookform/resolvers/zod'
import { capitalize } from '@common/utils/stringUtils'

export interface CreateQueryDrawerFormData extends CreateQueryFormData {
  study_site_id: string
  query_template: string
}

export interface CreateQueryDrawerProps {
  studyId: string
  open: boolean
  onClose: () => void
  onSubmit: (data: CreateQueryDrawerFormData) => void
  subjectId?: string
  qcTaskId?: string
}

export default function CreateQueryDrawer({
  open,
  onClose,
  onSubmit,
  studyId,
  subjectId,
  qcTaskId,
}: Readonly<CreateQueryDrawerProps>) {
  const [detailsExpanded, setDetailsExpanded] = useState(true)
  const [messageExpanded, setMessageExpanded] = useState(true)
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false)
  const [selectedQcTaskId, setSelectedQcTaskId] = useState<string>('')
  const [selectedSiteId, setSelectedSiteId] = useState<string>('')
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>(subjectId ?? '')
  const [selectedTimepointId, setSelectedTimepointId] = useState<string>('')

  // Suppresses dependent-field cleanup when values are populated automatically
  // (e.g. derived from the selected subject/QC task).
  const skipSiteCleanup = useRef(false)
  const skipSubjectCleanup = useRef(false)
  const lastAutofilledQcTaskId = useRef('')

  const defaultValues: CreateQueryFormData = useMemo(
    () => ({
      study_qc_task_id: qcTaskId || '',
      study_site_id: '',
      study_subject_id: subjectId ?? '',
      study_timepoint_submission_id: '',
      query_template: '',
      category: '',
      study_timepoint_container_submission_ids: [],
      query_assignee_ids: [],
      cc_user_ids: [],
      initial_message_title: '',
      initial_message_body: '',
      requires_timepoint_resubmission: false,
    }),
    [qcTaskId, subjectId],
  )

  const methods = useForm<CreateQueryFormData>({
    defaultValues: defaultValues,
    resolver: zodResolver(CreateQueryValidationSchema),
  })

  const { control, handleSubmit, reset, setValue, watch } = methods

  const selectedQcTask = watch('study_qc_task_id')
  const selectedSite = watch('study_site_id')
  const selectedSubject = watch('study_subject_id')
  const selectedTimepoint = watch('study_timepoint_submission_id')

  // Drive metadata filters from form values directly so Site/Subject/Timepoint changes
  // immediately refetch (avoids a stale selectedSiteId state lag).
  const { data: metadata } = useStudyQueryMetadata(
    {
      study_id: studyId,
      qc_task_id: selectedQcTask || undefined,
      site_id: selectedSite || undefined,
      subject_id: selectedSubject || undefined,
      timepoint_submission_id: selectedTimepoint || undefined,
    },
    {
      enabled: !!studyId && open,
    },
  )

  useEffect(() => {
    if (selectedQcTask !== selectedQcTaskId) {
      setSelectedQcTaskId(selectedQcTask ?? '')

      // Selecting a QC task overwrites visit context so it can be re-derived from
      // the chosen task metadata. Clearing the QC task leaves the visit fields alone.
      if (selectedQcTask) {
        setValue('study_site_id', '')
        setValue('study_subject_id', '')
        setValue('study_timepoint_submission_id', '')
        setValue('study_timepoint_container_submission_ids', [])
        setValue('query_assignee_ids', [])
        setValue('cc_user_ids', [])
        setSelectedSiteId('')
        setSelectedSubjectId('')
        setSelectedTimepointId('')
      }
    }
  }, [selectedQcTask, selectedQcTaskId, setValue])

  useEffect(() => {
    if (selectedSubject !== selectedSubjectId) {
      setSelectedSubjectId(selectedSubject ?? '')

      if (skipSubjectCleanup.current) {
        // Subject was auto-populated from the QC task; keep the timepoint.
        skipSubjectCleanup.current = false
      } else {
        // Changing the subject invalidates the timepoint (and its containers).
        setValue('study_timepoint_submission_id', '')
        setValue('study_timepoint_container_submission_ids', [])
        setSelectedTimepointId('')
      }
    }
  }, [selectedSubject, selectedSubjectId, setValue])

  useEffect(() => {
    if (selectedSite !== selectedSiteId) {
      setSelectedSiteId(selectedSite)

      setValue('study_timepoint_container_submission_ids', [])
      setValue('query_assignee_ids', [])
      setValue('cc_user_ids', [])

      if (skipSiteCleanup.current) {
        // Site was auto-populated from the subject/QC task; keep the visit context.
        skipSiteCleanup.current = false
      } else {
        // User changed the site manually, so subjects are filtered differently now.
        setValue('study_subject_id', '')
        setValue('study_timepoint_submission_id', '')
        setSelectedSubjectId('')
        setSelectedTimepointId('')
      }
    }
  }, [selectedSite, selectedSiteId, setValue])

  useEffect(() => {
    if (selectedTimepoint !== selectedTimepointId) {
      setSelectedTimepointId(selectedTimepoint ?? '')
      // Changing the timepoint invalidates container selections for the previous visit.
      setValue('study_timepoint_container_submission_ids', [])
    }
  }, [selectedTimepoint, selectedTimepointId, setValue])

  // Prefill Site / Subject / Timepoint from the selected QC task metadata once (still editable).
  useEffect(() => {
    if (!selectedQcTaskId) {
      lastAutofilledQcTaskId.current = ''
      return
    }
    if (lastAutofilledQcTaskId.current === selectedQcTaskId || !metadata?.qc_tasks?.length) {
      return
    }

    const qcTask = metadata.qc_tasks.find((task) => task.id === selectedQcTaskId)
    if (!qcTask) {
      return
    }

    if (qcTask.study_site_id) {
      skipSiteCleanup.current = true
      setValue('study_site_id', qcTask.study_site_id)
    }
    if (qcTask.study_subject_id) {
      skipSubjectCleanup.current = true
      setValue('study_subject_id', qcTask.study_subject_id)
    }
    if (qcTask.study_timepoint_submission_id) {
      setValue('study_timepoint_submission_id', qcTask.study_timepoint_submission_id)
    }
    lastAutofilledQcTaskId.current = selectedQcTaskId
  }, [selectedQcTaskId, metadata?.qc_tasks, setValue])

  // Keep Site in sync with the selected subject when no QC task drives the visit context.
  // Selecting Subject first is allowed; Site auto-fills when the subject maps to one site.
  useEffect(() => {
    if (!selectedSubjectId || selectedQcTaskId || !metadata?.sites) {
      return
    }

    if (metadata.sites.length === 1) {
      const soleSiteId = metadata.sites[0].id
      if (selectedSite !== soleSiteId) {
        skipSiteCleanup.current = true
        setValue('study_site_id', soleSiteId)
      }
      return
    }

    // Multiple (or zero) sites for this subject: clear Site if it is no longer valid.
    if (selectedSite && !metadata.sites.some((site) => site.id === selectedSite)) {
      skipSiteCleanup.current = true
      setValue('study_site_id', '')
    }
  }, [metadata?.sites, selectedSite, selectedSubjectId, selectedQcTaskId, setValue])

  useEffect(() => {
    if (!open) {
      reset(defaultValues)
      setSelectedSiteId('')
      setSelectedSubjectId(subjectId ?? '')
      setSelectedTimepointId('')
      setIsCloseConfirmOpen(false)
      skipSiteCleanup.current = false
      skipSubjectCleanup.current = false
      lastAutofilledQcTaskId.current = ''
    }
  }, [open, reset, defaultValues, subjectId])

  const toggleDetailsSection = () => {
    setDetailsExpanded(!detailsExpanded)
  }

  const toggleMessageSection = () => {
    setMessageExpanded(!messageExpanded)
  }

  const onFormSubmit = (data: CreateQueryDrawerFormData) => {
    const submissionData = {
      ...data,
      initial_message_title: data.initial_message_title,
      cc_user_ids: data.cc_user_ids ?? [],
    }
    onSubmit(submissionData)
  }

  const handleCancel = () => {
    onClose()
  }

  const handleDrawerClose = (_event: unknown, reason?: string) => {
    if (reason === 'backdropClick') {
      setIsCloseConfirmOpen(true)
      return
    }
    onClose()
  }

  const handleConfirmClose = () => {
    setIsCloseConfirmOpen(false)
    onClose()
  }

  const handleCancelCloseConfirm = () => {
    setIsCloseConfirmOpen(false)
  }

  const categoryOptions = useMemo(() => {
    return metadata?.query_templates?.map((queryTemplate) => {
      const label = `${capitalize(queryTemplate.category)}: ${queryTemplate.title}`
      return {
        label,
        value: label,
        additional_fields: {
          template: queryTemplate.template,
          category: queryTemplate.category,
        },
      }
    })
  }, [metadata?.query_templates])

  const qcTaskOptions = useMemo(() => {
    return (
      metadata?.qc_tasks?.map((task) => ({
        label: task.name,
        value: task.id,
      })) ?? []
    )
  }, [metadata?.qc_tasks])

  const siteOptions = useMemo(() => {
    return (
      metadata?.sites?.map((site) => ({
        label: `${site.pseudo_id}-${site.name}`,
        value: site.id,
      })) ?? []
    )
  }, [metadata?.sites])

  const subjectOptions = useMemo(() => {
    return (
      metadata?.subjects?.map((subject) => ({
        label: subject.pseudo_id,
        value: subject.id,
      })) ?? []
    )
  }, [metadata?.subjects])

  const timepointOptions = useMemo(() => {
    return selectedSubjectId && metadata?.timepoints
      ? metadata.timepoints.map((timepoint) => ({
          label: timepoint.name,
          value: timepoint.id,
        }))
      : []
  }, [selectedSubjectId, metadata?.timepoints])

  const containerOptions = useMemo(() => {
    return selectedSiteId && selectedTimepointId && metadata?.timepoint_container_submissions
      ? metadata.timepoint_container_submissions.map((submission) => ({
          label: submission.name,
          value: submission.id,
        }))
      : []
  }, [selectedSiteId, selectedTimepointId, metadata?.timepoint_container_submissions])

  const assigneeOptions = useMemo(() => {
    return selectedSiteId && metadata?.assignee_users
      ? metadata.assignee_users.map((user) => ({
          label: user.email ? `${user.name} (${user.email})` : user.name,
          value: user.id,
          description: user.role,
        }))
      : []
  }, [selectedSiteId, metadata?.assignee_users])

  const ccUserOptions = useMemo(() => {
    return selectedSiteId && metadata?.cc_users
      ? metadata.cc_users.map((user) => ({
          label: user.email ? `${user.name} (${user.email})` : user.name,
          value: user.id,
          description: user.role,
        }))
      : []
  }, [selectedSiteId, metadata?.cc_users])

  const handleCategoryChange = (selectedCategory: Option) => {
    // Persist the full "Category: Title" selection so details/list can show the complete label.
    methods.setValue('category', selectedCategory.label)
    methods.setValue('query_template', selectedCategory.value)
    methods.setValue('initial_message_title', selectedCategory.label)
    methods.setValue('initial_message_body', (selectedCategory.additional_fields?.template as string) || '')
  }

  const resubmissionOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ]

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleDrawerClose}
        slotProps={{
          paper: {
            sx: {
              width: SIZING.drawerWidth,
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingX: SPACING.drawerHeaderSpacingX,
            paddingY: SPACING.drawerHeaderSpacingY,
            borderBottom: '1px solid',
            borderColor: 'secondary.main',
          }}
        >
          <Typography variant="h6">New query</Typography>
          <IconButton onClick={handleCancel} edge="end" aria-label="close">
            <CloseIcon sx={{ color: 'text.primary' }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            overflowY: 'auto',
            flexGrow: 1,
          }}
        >
          <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
            <Box
              sx={{
                paddingX: SPACING.drawerHeaderSpacingX,
              }}
            >
              <Stack
                justifyContent="space-between"
                alignItems="center"
                direction="row"
                sx={{ paddingY: SPACING.spacingLg, cursor: 'pointer' }}
                onClick={toggleDetailsSection}
              >
                <Typography variant="subtitle1">Details</Typography>
                {detailsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Stack>
              <Collapse in={detailsExpanded}>
                <Box sx={{ paddingBottom: detailsExpanded ? SPACING.spacingXl : 0 }}>
                  <Stack spacing={SPACING.formFieldsSpacing}>
                    <Dropdown
                      name="study_qc_task_id"
                      label="QC Task (Optional)"
                      options={qcTaskOptions}
                      control={control}
                      size="small"
                      disabled={!metadata}
                    />

                    <Dropdown
                      name="study_site_id"
                      label="Site"
                      options={siteOptions}
                      control={control}
                      size="small"
                      disabled={!metadata}
                    />

                    <Dropdown
                      name="study_subject_id"
                      label="Subject ID"
                      options={subjectOptions}
                      control={control}
                      size="small"
                      disabled={!metadata}
                    />

                    <Dropdown
                      name="study_timepoint_submission_id"
                      label="Timepoint"
                      options={timepointOptions}
                      control={control}
                      size="small"
                      disabled={!metadata}
                    />

                    <Dropdown
                      name="query_template"
                      label="Category"
                      options={categoryOptions ?? []}
                      control={control}
                      size="small"
                      onChange={(_, selectedCategory) => handleCategoryChange(selectedCategory as Option)}
                    />

                    <Dropdown
                      name="study_timepoint_container_submission_ids"
                      label="Container Submissions (Optional)"
                      options={containerOptions}
                      control={control}
                      size="small"
                      multiple
                      disabled={!metadata}
                    />

                    <Dropdown
                      name="query_assignee_ids"
                      label="Assignee(s)"
                      options={assigneeOptions}
                      control={control}
                      size="small"
                      multiple
                      helperText="Users who can act on the query"
                      disabled={!metadata}
                      showOptionDescription
                    />

                    <Dropdown
                      name="cc_user_ids"
                      label="CC User(s)"
                      options={ccUserOptions}
                      control={control}
                      size="small"
                      multiple
                      helperText="Users who can view the query"
                      disabled={!metadata}
                      showOptionDescription
                    />
                    <Stack direction="column" gap={SPACING.spacingSm}>
                      <Typography>Should this query reopen the timepoint for submission?</Typography>
                      <RadioGroup
                        name="requires_timepoint_resubmission"
                        options={resubmissionOptions}
                        control={control}
                        row
                      />
                    </Stack>
                  </Stack>
                </Box>
              </Collapse>
            </Box>

            <Divider />

            <Box
              sx={{
                paddingX: SPACING.drawerHeaderSpacingX,
              }}
            >
              <Stack
                justifyContent="space-between"
                alignItems="center"
                direction="row"
                sx={{ paddingY: SPACING.spacingLg, cursor: 'pointer' }}
                onClick={toggleMessageSection}
              >
                <Typography variant="subtitle1">Initial Message</Typography>
                {messageExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </Stack>
              <Collapse in={messageExpanded}>
                <Box sx={{ paddingBottom: messageExpanded ? SPACING.spacingXl : 0 }}>
                  <Stack spacing={SPACING.formFieldsSpacing}>
                    <TextField control={control} name="initial_message_title" label="Title (Optional)" size="small" />

                    <TextField control={control} name="initial_message_body" label="Message" multiline rows={6} />
                  </Stack>
                </Box>
              </Collapse>
            </Box>
          </form>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            padding: SPACING.formFieldsSectionSpacing,
            borderTop: '1px solid',
            borderColor: 'secondary.main',
          }}
        >
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit(onFormSubmit)}>
            Create Query
          </Button>
        </Box>
      </Drawer>
      <ConfirmationDialog
        open={isCloseConfirmOpen}
        title="Close query form?"
        description="Any information you have entered will be lost."
        confirmLabel="Close"
        cancelLabel="Cancel"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelCloseConfirm}
      />
    </>
  )
}
